import axios, { AxiosError } from 'axios';
import i18n from 'i18next';
import { toast } from 'sonner';
import { API_GATEWAY_BASE_URL, getResolvedApiBaseUrl } from '@/core/global-data/constants.data';
import store from '@/core/global-store';
import { logout } from '@/core/global-store/features/auth/authSlice';
import { setSession } from '@/features/auth/guard/utils';
import router from '@/core/global-router';

// ------------- AXIOS DEFAULTS SETTINGS -------------
// ---------------------------------------------------
const defaultURL = getResolvedApiBaseUrl() || API_GATEWAY_BASE_URL;

axios.defaults.baseURL = defaultURL;

/** Debounce repeated 401/403 toasts when many requests fail at once */
let lastAuthResponseToastAt = 0;
const AUTH_RESPONSE_TOAST_COOLDOWN_MS = 2800;

function shouldSkipAuthToastUrl(url: string): boolean {
    const u = url.toLowerCase();
    if (u.includes('/login')) return true;
    if (u.includes('/forgot-password')) return true;
    if (u.includes('/reset-password')) return true;
    if (u.includes('/public/app-settings')) return true;
    return false;
}

/** `/me` only — avoid toast when AuthInitializer clears session */
function isBootstrapMeRequest(url: string): boolean {
    return /(^|\/)me(\?|$|#)/i.test(url);
}

function maybeToastAuthResponse(status: number | undefined, requestUrl: string) {
    if (status !== 401 && status !== 403) return;
    if (!requestUrl || shouldSkipAuthToastUrl(requestUrl)) return;
    if (status === 401 && isBootstrapMeRequest(requestUrl)) return;

    const now = Date.now();
    if (now - lastAuthResponseToastAt < AUTH_RESPONSE_TOAST_COOLDOWN_MS) return;
    lastAuthResponseToastAt = now;

    if (status === 403) {
        toast.error(i18n.t('api_forbidden_toast'), { id: 'api-response-403', duration: 6500 });
    } else {
        toast.error(i18n.t('api_unauthorized_toast'), { id: 'api-response-401', duration: 6500 });
    }
}

// ------------- GLOBAL ERROR HANDLER -------------

/** Explicit session teardown (e.g. user clicks Logout). Not invoked automatically on API 401/403. */
export const clearSessionAndGoToLogin = () => {
    setSession(null);
    store.dispatch(logout());
    router.navigate('/login', { replace: true });
};

// ------------- CREATE AXIOS INTERCEPTORS -------------
// ----------------------------------------------------

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;
        const cfg = error.config;
        const path = String(cfg?.url ?? '');
        const base = String(cfg?.baseURL ?? '');
        const resolved =
            path === ''
                ? ''
                : path.startsWith('http')
                  ? path
                  : `${base.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
        const url =
            resolved ||
            String((error.request as XMLHttpRequest | undefined)?.responseURL ?? '');
        maybeToastAuthResponse(status, url);

        return Promise.reject(error);
    },
);

// -------------------------------------------------------

export const endpoints = {
    dashboard: {
        analytics: '/dashboard',
        cars: '/dashboard-overview',
    },
    auth: {
        login: '/login',
        profile: '/user',
        me: '/me',
        newPassword: '/new-password',
        forgotPassword: '/forgot-password',
        resetPassword: '/reset-password',
    },
    // 
    notification: {
        count: '/notification-count',
        list: '/notification-list',
        read: '/notification-read/',
        delete: '/notification-delete/',
        allRead: '/notification-all-read',
    },
    task: {
        list: '/task-list',
        show: '/task-show/',
        update: '/task-update/',
        create: '/task-create',
        delete: '/task-delete/',
        pagination: '/task-list-paginate',
        dashboard: '/task-dashboard',
        pointsSummary: '/task-points-summary/',
        employeeReport: '/task-employee-report/',
    },
    reward: {
        create: '/reward-create',
        list: '/reward-list',
        show: '/reward-show/',
        update: '/reward-update/',
        delete: '/reward-delete/',
        pagination: '/reward-list-paginate',
        dashboard: '/reward-dashboard',
    },
    deduction: {
        create: '/deduction-create',
        list: '/deduction-list',
        show: '/deduction-show/',
        update: '/deduction-update/',
        delete: '/deduction-delete/',
        pagination: '/deduction-list-paginate',
        dashboard: '/deduction-dashboard',
    },
    program: {
        list: '/program-list',
        show: '/program-show/',
        update: '/program-update/',
        create: '/program-create',
        delete: '/program-delete/',
        pagination: '/program-list-paginate',
        dashboard: '/program-dashboard',
    },
    holidays: {
        list: '/holiday-request-list-paginate',
    },
    career: {
        list: '/career-list',
        show: '/career-show/',
        update: '/career-update/',
        create: '/career-create',
        delete: '/career-delete/',
    },
    applicant: {
        dashboard: '/applicant-dashboard',
        list: '/applicant-list',
        show: '/applicant-show/',
        update: '/applicant-update/',
        create: '/applicant-create',
        delete: '/applicant-delete/',
        pagination: '/applicant-list-paginate',
    },
    job: {
        list: '/job-list',
        show: '/job-show/',
        update: '/job-update/',
        create: '/job-create',
        delete: '/job-delete/',
        pagination: '/job-list-paginate',
        dashboard: '/job-dashboard',
    },
    interview: {
        dashboard: '/interview-dashboard',
        list: '/interview-list',
        show: '/interview-show/',
        byApplicant: '/interview-by-applicant/',
        update: '/interview-update/',
        rejectionNotify: '/interview-rejection-notify/',
        acceptNotify: '/interview-accept-notify/',
        create: '/interview-create',
        delete: '/interview-delete/',
        pagination: '/interview-list-paginate',
    },
    review: {
        dashboard: '/review-dashboard',
        list: '/review-list',
        show: '/review-show/',
        update: '/review-update/',
        create: '/review-create',
        delete: '/review-delete/',
    },
    payroll: {
        dashboard: '/payroll-dashboard',
        list: '/payroll-list',
        show: '/payroll-show/',
        update: '/payroll-update/',
        create: '/payroll-create',
        delete: '/payroll-delete/',
        pagination: '/payroll-list-paginate',
        /** Monthly salary sheet runs (Laravel payroll module) */
        runList: '/payroll-run-list',
        runCreate: '/payroll-run-create',
        runShow: '/payroll-run-show/',
        runDelete: '/payroll-run-delete/',
        runGenerate: '/payroll-run-generate/',
        runEmployeePayrolls: '/payroll-run-employee-payrolls/',
        runApprove: '/payroll-run-approve/',
        runUnlock: '/payroll-run-unlock/',
        runExportCsv: '/payroll-run-export-csv/',
        employeePayrollShow: '/employee-payroll-show/',
        employeePayrollUpdate: '/employee-payroll-update/',
        employeePayrollRecalculate: '/employee-payroll-recalculate/',
        employeePayrollManualItem: '/employee-payroll-manual-item/',
        employeePayrollManualItemDelete: '/employee-payroll-manual-item/',
        overtimeTypeList: '/overtime-type-list',
        overtimeTypeUpdate: '/overtime-type-update/',
    },
    allowance: {
        paginate: '/allowance-list-paginate',
        list: '/allowance-list',
        show: '/allowance-show/',
        update: '/allowance-update/',
        create: '/allowance-create',
        delete: '/allowance-delete/',
        dashboard: '/allowance-dashboard',
    },
    trainee: {
        dashboard: '/trainee-dashboard',
        list: '/trainee-list',
        show: '/trainee-show/',
        update: '/trainee-update/',
        create: '/trainee-create',
        delete: '/trainee-delete/',
        reviews: '/trainee-reviews',
        reviewsPaginate: '/trainee-reviews-paginate',
        reviewsShow: '/trainee-reviews-show/',
        reviewsUpdate: '/trainee-reviews-update/',
        reviewsCreate: '/trainee-reviews-create',
        reviewsDelete: '/trainee-reviews-delete/',
        reviewsList: '/trainee-reviews-list',
    },
    shifts: {
        list: '/shifts-list',
        listPaginate: '/shifts-list-paginate',
        show: '/shifts-show/',
        update: '/shifts-update/',
        create: '/shifts-create',
        delete: '/shifts-delete/',
        dashboard: '/shifts-dashboard',
    },
    // 
    cars: {
        list: '/vehicle-list',
        pagination: '/vehicle-list-paginate',
        show: '/vehicle-show/',
        create: '/vehicle-create',
        delete: '/vehicle-delete/',
        update: '/vehicle-update/',
        media: '/media',
    },
    models: {
        list: '/model-list',
        show: '/model-show',
        create: '/model-create',
    },
    brands: {
        list: '/brand-list',
        show: '/brand-show',
        create: '/brand-create',
        brandAndModelCreate: '/brand-and-model-create',
    },
    fleet: {
        create: '/fleet-create',
    },
    branch: {
        list: '/branch-list',
        show: '/branch-show/',
        update: '/branch-update/',
        delete: '/branch-delete/',
        create: '/branch-create',
        dashboard: 'branch-dashboard',
    },
    customer: {
        list: '/customer-list',
        show: '/customer-show/',
        update: '/customer-update/',
        delete: '/customer-delete/',
        create: '/customer-create',
        images: '/customer-update-personal-images/',
        license: '/customer-update-license-images/',
        dashboard: '/customer-dashboard',
        dashboardNewCustomers: '/customer-new-dashboard',
        block: '/customer-block/',
        data: '/customer-contracts/',
        /** Kanban history — see docs/apis/hr/NEW_ENDPOINTS.md §5.5 */
        typeTrack: '/customer-type-track/',
    },
    departments: {
        list: '/department-list-paginate',
        show: '/department-show/',
        create: '/department-create',
        update: '/department-update/',
        delete: '/department-delete/',
        dashboard: '/department-dashboard',
    },
    managers: {
        departmentList: '/department-manager-list',
        departmentUpdate: '/department-manager-update/',
        branchList: '/branch-manager-list',
        branchUpdate: '/branch-manager-update/',
    },
    employee: {
        list: '/employee-list-paginate',
        simpleList: '/employee-list',
        show: '/employee-show/',
        create: '/employee-create',
        update: '/employee-update/',
        delete: '/employee-delete/',
        loginAs: '/employee-login-as/',
        attendance: '/employee-attendance',
        dashboard: '/employee-dashboard',
        salary: '/employee-salary/',
        salaryList: '/employee-salary-list',
        entryExit: '/employee-entry-exit',
    },
    holiday: {
        list: '/holiday-request-list-paginate',
        show: '/holiday-request-show/',
        createWithAdmin: '/holiday-range-create',
        create: '/holiday-request-create',
        update: '/holiday-request-update/',
        delete: '/holiday-request-delete/',
        dashboard: '/holiday-request-dashboard',
    },
    employeeAttendance: {
        list: '/attendance-list',
        show: '/attendance-show/',
        create: '/attendance-create',
        update: '/attendance-update/',
        delete: '/attendance-delete/',
        analysis: '/attendance-employee-stats',
        attendance: '/attendance-monthly-stats',
        checkIn: '/attendance-check-in/',
        checkOut: '/attendance-check-out/',
    },
    cutomerContact: {
        list: '/customer-contact-list',
        create: '/customer-contact-create',
        dashboard: '/customer-contact-dashboard',
    },
    complaint: {
        list: '/complaint-list',
        show: '/complaint-show/',
        update: '/complaint-update/',
        delete: '/complaint-delete/',
        create: '/complaint-create',
        resolve: '/complaint-resolve/',
        dashboard: '/complaint-dashboard',
    },
    maintenance: {
        list: '/maintenance-list',
        show: '/maintenance-show/',
        update: '/maintenance-update/',
        delete: '/maintenance-delete/',
        create: '/maintenance-create',
        byVehicle: '/maintenances/vehicle/',
        dashboard: '/maintenance-dashboard',
        dashboardChart: '/maintenance-maintenance-dashboard',
        dashboardVehicleChart: '/maintenance-vehicle-dashboard',
    },
    maintenanCenter: {
        list: '/maintenance-center-list',
        show: '/maintenance-center-show/',
        update: '/maintenance-center-update/',
        create: '/maintenance-center-create',
        delete: '/maintenance-center-delete/',
        select: '/maintenance-center-select',
        dashboard: '/maintenance-center-dashboard',
    },


    hrContract: {
        paginate: '/contract-list-paginate',
        show: '/contract-show/',
        update: '/contract-update/',
        create: '/contract-create',
        delete: '/contract-delete/',
        payrollPreview: '/contract-payroll-preview/',
        payrollPreviewDraft: '/contract-payroll-preview',
    },
    employeePenalty: {
        list: (employeeId: string | number) => `/employee-penalties/${employeeId}`,
        create: '/employee-penalty-create',
        update: (id: string | number) => `/employee-penalty-update/${id}`,
        delete: (id: string | number) => `/employee-penalty-delete/${id}`,
    },
    contract: {
        paginate: '/customer-contract-list-paginate',
        list: '/customer-contract-list',
        show: '/customer-contract-show/',
        update: '/customer-contract-update/',
        create: '/customer-contract-create',
        delete: '/customer-contract-delete/',
        approved: '/customer-contract-approved/',
        rejected: '/customer-contract-rejected/',
        uploadContract: '/customer-contract-upload-contract-file/',
    },
    insurance: {
        paginate: '/insurance-list-paginate',
        list: '/insurance-list',
        show: '/insurance-show/',
        /** Current authenticated user's insurance (employee matched by user email). */
        employeeMe: '/insurance-employee-me',
        employeeShow: (id: string | number) => `/insurance-employee-show/${id}`,
        update: '/insurance-update/',
        create: '/insurance-create',
        delete: '/insurance-delete/',
    },
    insuranceRelatives: {
        paginate: '/insurance-relative-list-paginate',
        show: '/insurance-relative-show/',
        update: '/insurance-relative-update/',
        create: '/insurance-relative-create',
        delete: '/insurance-relative-delete/',
    },
    loan: {
        dashboard: '/loan-request-dashboard',
        list: '/loan-request-list-paginate',
        show: '/loan-request-show/',
        update: '/loan-request-update/',
        create: '/loan-request-create',
        delete: '/loan-request-delete/',
    },
    loanType: {
        list: '/loan-type-list',
        create: '/loan-type-create',
        update: '/loan-type-update/',
    },
    /** Same resource as `loan` — backend exposes `loan-request-*` only. */
    personalLoan: {
        dashboard: '/loan-request-dashboard',
        list: '/loan-request-list-paginate',
        show: '/loan-request-show/',
        update: '/loan-request-update/',
        create: '/loan-request-create',
        delete: '/loan-request-delete/',
    },
    letterRequests: {
        paginate: '/letter-request-list-paginate',
        create: '/letter-request-create',
        update: '/letter-request-update/',
        delete: '/letter-request-delete/',
    },
    extraServices: {
        list: '/service-request-list-paginate',
        show: '/service-request-show/',
        create: '/service-request-create',
        update: '/service-request-update/',
        delete: '/service-request-delete/',
    },
    employeeComplaint: {
        list: '/employee-complaint-list-paginate',
        show: '/employee-complaint-show/',
        update: '/employee-complaint-update/',
        create: '/employee-complaint-create',
        delete: '/employee-complaint-delete/',
    },
    /** كشوف المرتب والبدلات والخصومات والحساب البنكي للموظف المسجّل دخوله (مطابقة البريد مع سجل الموظف). */
    financeEmployee: {
        payrollStatementsMe: '/finance-payroll-statements-me',
        deductionsMe: '/finance-employee-deductions-me',
        allowancesMe: '/finance-employee-allowances-me',
        bankAccountsMe: '/finance-employee-bank-accounts-me',
        benefitsMe: '/finance-employee-benefits-me',
    },
    appSettings: {
        public: '/public/app-settings',
        show: '/app-settings',
        update: '/app-settings',
        clearCache: '/app-settings/clear-cache',
    },
    accessControl: {
        roleList: '/role-list',
        roleCreate: '/role-create',
        roleUpdate: '/role-update/',
        roleDelete: '/role-delete/',
        rolePermissions: '/role-permissions/',
        permissionList: '/permission-list',
        roleAssignPermissions: '/role-assign/permissions/',
        userAssignRole: '/user-assign-role/',
        roleSelectList: '/role-select-list',
        userRoleAssignmentList: '/user-role-assignment-list',
    },
    subscriptions: {
        publicPlans: '/public/subscription-plans',
        publicPlatformSettings: '/public/platform-settings',
        mySubscription: '/my-subscription',
        plansAvailable: '/subscription-plans-available',
        upgradeRequest: '/subscription-upgrade-request',
        planList: '/subscription-plan-list',
        planCreate: '/subscription-plan-create',
        planUpdate: '/subscription-plan-update/',
        planDelete: '/subscription-plan-delete/',
        tenantList: '/tenant-subscription-list',
        tenantCreate: '/tenant-subscription-create',
        tenantUpdate: '/tenant-subscription-update/',
        tenantEnd: '/tenant-subscription-end/',
        tenantBlock: '/tenant-subscription-block/',
        tenantUnblock: '/tenant-subscription-unblock/',
        tenantDelete: '/tenant-subscription-delete/',
        tenantLoginAs: '/tenant-subscription-login-as/',
        upgradeRequestList: '/subscription-upgrade-request-list',
        upgradeRequestUpdate: '/subscription-upgrade-request-update/',
        platformSettings: '/platform-settings',
    },
};
